/**
 * Palete de culori per poluant, aliniate cu European Air Quality Index (EEA).
 * Fiecare paletă folosește praguri fixe (nu percentile) — comparabile între run-uri.
 * Include și palete meteo (temperatură, viteză vânt).
 */

// Categorii AQI EEA
export const AQI_CATEGORIES = [
  { label: 'Bun',          color: '#50c878' },
  { label: 'Acceptabil',   color: '#a3d65c' },
  { label: 'Moderat',      color: '#ffd700' },
  { label: 'Slab',         color: '#ff8c00' },
  { label: 'Foarte slab',  color: '#ff4444' },
  { label: 'Extrem',       color: '#8b0000' },
];

/**
 * European Air Quality Index (EAQI) — official EEA 2024 bands.
 * Source: https://airindex.eea.europa.eu/AQI/index.html
 * Method: "worst-case" / dominant pollutant approach.
 * For each pollutant, compute a sub-index (0–5), then take the maximum.
 *
 * Thresholds are upper bounds for each band (µg/m³, hourly means).
 * Band 0=Good, 1=Fair, 2=Moderate, 3=Poor, 4=VeryPoor, 5=ExtremelyPoor
 */
export const EAQI_BANDS = {
  pm25: [5, 15, 50, 90, 140],    // 0-5, 6-15, 16-50, 51-90, 91-140, >140
  pm10: [15, 45, 120, 195, 270], // 0-15, 16-45, 46-120, 121-195, 196-270, >270
  o3:   [60, 100, 120, 160, 180],// 0-60, 61-100, 101-120, 121-160, 161-180, >180
  no2:  [10, 25, 60, 100, 150],  // 0-10, 11-25, 26-60, 61-100, 101-150, >150
  so2:  [20, 40, 125, 190, 275], // 0-20, 21-40, 41-125, 126-190, 191-275, >275
};

/**
 * Compute the EAQI composite index from per-pollutant average values.
 * @param {Object} values - { pm25: number|null, pm10: number|null, o3: number|null, no2: number|null, so2: number|null }
 * @returns {{ level: number, label: string, color: string, dominantPollutant: string }}
 */
export function computeEAQI(values) {
  let worstLevel = 0;
  let dominantPollutant = 'pm25';
  const pollutants = ['pm25', 'pm10', 'o3', 'no2', 'so2'];

  for (const p of pollutants) {
    const v = values[p];
    if (v == null || isNaN(v)) continue;

    const bands = EAQI_BANDS[p];
    let level = 5; // Extremely poor by default
    for (let i = 0; i < bands.length; i++) {
      if (v <= bands[i]) {
        level = i;
        break;
      }
    }

    if (level > worstLevel) {
      worstLevel = level;
      dominantPollutant = p;
    }
  }

  return {
    level: worstLevel,
    label: AQI_CATEGORIES[Math.min(worstLevel, AQI_CATEGORIES.length - 1)].label,
    color: AQI_CATEGORIES[Math.min(worstLevel, AQI_CATEGORIES.length - 1)].color,
    dominantPollutant,
  };
}

/**
 * Get the EAQI color (RGB) for a given composite level (0-5).
 */
export function eaqiLevelToColor(level) {
  const cat = AQI_CATEGORIES[Math.min(level, AQI_CATEGORIES.length - 1)];
  return hexToRgb(cat.color);
}

// Praguri per variabilă — AQ + meteo
export const THRESHOLDS = {
  no2:  [0, 40, 90, 120, 230, 340, 500],
  o3:   [0, 50, 100, 130, 240, 380, 600],
  pm25: [0, 10, 20, 25, 50, 75, 150],
  pm10: [0, 20, 40, 50, 100, 150, 300],
  so2:  [0, 100, 200, 350, 500, 750, 1250],
  co:   [0, 2000, 4000, 7000, 10000, 20000, 40000],
  // Meteo
  temp2m:     [-10, 0, 10, 20, 30, 40, 50],
  wind_speed: [0, 2, 5, 10, 15, 25, 40],
  cloud:      [0, 10, 25, 50, 75, 90, 100],
  precip:     [0, 0.5, 2, 5, 10, 25, 50],
};

// Unități
export const UNITS = {
  no2: 'µg/m³', o3: 'µg/m³', pm25: 'µg/m³',
  pm10: 'µg/m³', so2: 'µg/m³', co: 'µg/m³',
  temp2m: '°C', wind_speed: 'm/s',
  cloud: '%', precip: 'mm',
};

// Labels scurte
export const LABELS = {
  no2: 'NO₂', o3: 'O₃', pm25: 'PM2.5',
  pm10: 'PM10', so2: 'SO₂', co: 'CO',
  temp2m: 'Temperatură', wind_speed: 'Viteză vânt',
  cloud: 'Nebulozitate', precip: 'Precipitații',
};

// ── Palete de culori meteo ───────────────────────────────────────

// Temperatură: albastru intens → cyan → verde → galben → portocaliu → roșu
const TEMP_COLORS = [
  { val: -10, rgb: [50,  20,  180] },
  { val:   0, rgb: [70,  130, 220] },
  { val:  10, rgb: [80,  200, 200] },
  { val:  18, rgb: [100, 220, 100] },
  { val:  25, rgb: [220, 220, 50 ] },
  { val:  32, rgb: [255, 160, 30 ] },
  { val:  40, rgb: [220, 50,  30 ] },
  { val:  50, rgb: [160, 20,  80 ] },
];

// Viteză vânt: albastru pal → verde → galben → portocaliu → roșu
const WIND_SPEED_COLORS = [
  { val: 0,  rgb: [200, 230, 255] },
  { val: 2,  rgb: [100, 200, 240] },
  { val: 5,  rgb: [60,  210, 160] },
  { val: 10, rgb: [200, 230, 60 ] },
  { val: 15, rgb: [255, 180, 40 ] },
  { val: 25, rgb: [255, 80,  40 ] },
  { val: 40, rgb: [200, 40,  160] },
];

// Nebulozitate: transparent → alb-gri
const CLOUD_COLORS = [
  { val: 0,   rgb: [40,  60,  90 ] },
  { val: 10,  rgb: [80,  100, 140] },
  { val: 25,  rgb: [120, 140, 170] },
  { val: 50,  rgb: [160, 170, 190] },
  { val: 75,  rgb: [190, 200, 210] },
  { val: 90,  rgb: [210, 215, 220] },
  { val: 100, rgb: [230, 235, 240] },
];

// Precipitații: galben pal → albastru → mov
const PRECIP_COLORS = [
  { val: 0,   rgb: [40,  80,  60 ] },
  { val: 0.5, rgb: [100, 200, 140] },
  { val: 2,   rgb: [80,  200, 220] },
  { val: 5,   rgb: [60,  140, 240] },
  { val: 10,  rgb: [80,  80,  220] },
  { val: 25,  rgb: [160, 60,  200] },
  { val: 50,  rgb: [220, 40,  120] },
];

// Categorii meteo (pentru legendă)
const TEMP_CATEGORIES = [
  { label: 'Geros',   color: '#3214B4' },
  { label: 'Rece',    color: '#4682DC' },
  { label: 'Răcoare', color: '#50C8C8' },
  { label: 'Plăcut',  color: '#64DC64' },
  { label: 'Cald',    color: '#DCD032' },
  { label: 'Fierbinte', color: '#DC3220' },
];

const WIND_SPEED_CATEGORIES = [
  { label: 'Calm',     color: '#C8E6FF' },
  { label: 'Ușor',     color: '#64C8F0' },
  { label: 'Moderat',  color: '#3CD2A0' },
  { label: 'Tare',     color: '#C8E63C' },
  { label: 'Puternic', color: '#FFB428' },
  { label: 'Furtună',  color: '#FF5028' },
];

const CLOUD_CATEGORIES = [
  { label: 'Senin',      color: '#283C5A' },
  { label: 'Parțial',    color: '#788CAA' },
  { label: 'Variabil',   color: '#A0AABE' },
  { label: 'Înnorat',    color: '#C0C8D2' },
  { label: 'Acoperit',   color: '#D2D8DC' },
  { label: 'Total noros', color: '#E6EBF0' },
];

const PRECIP_CATEGORIES = [
  { label: 'Uscat',    color: '#285038' },
  { label: 'Ușoară',   color: '#64C88C' },
  { label: 'Moderată', color: '#50C8DC' },
  { label: 'Intensă',  color: '#3C8CF0' },
  { label: 'Torențial', color: '#A03CC8' },
  { label: 'Extremă',  color: '#DC2878' },
];

export function getCategoriesForVariable(variable) {
  if (variable === 'temp2m') return TEMP_CATEGORIES;
  if (variable === 'wind_speed') return WIND_SPEED_CATEGORIES;
  if (variable === 'cloud') return CLOUD_CATEGORIES;
  if (variable === 'precip') return PRECIP_CATEGORIES;
  return AQI_CATEGORIES;
}

/**
 * Interpolează între culorile pe baza valorii și pragurilor.
 * Funcționează atât pentru AQ cât și pentru meteo.
 */
export function valueToColor(value, variable) {
  if (value == null || isNaN(value)) return [0, 0, 0, 0];

  // Folosim paleta meteo dedicată dacă e disponibilă
  const specialPalette = variable === 'temp2m' ? TEMP_COLORS
                       : variable === 'wind_speed' ? WIND_SPEED_COLORS
                       : variable === 'cloud' ? CLOUD_COLORS
                       : variable === 'precip' ? PRECIP_COLORS
                       : null;

  if (specialPalette) {
    return interpolateSpecial(value, specialPalette);
  }

  // AQ: interpolez pe categorii EEA
  const thresholds = THRESHOLDS[variable];
  if (thresholds == null) return [128, 128, 128, 200];

  const colors = AQI_CATEGORIES.map(c => hexToRgb(c.color));

  for (let i = 0; i < thresholds.length - 1; i++) {
    if (value <= thresholds[i + 1]) {
      const t = (value - thresholds[i]) / (thresholds[i + 1] - thresholds[i]);
      const c0 = colors[Math.min(i, colors.length - 1)];
      const c1 = colors[Math.min(i + 1, colors.length - 1)];
      return [
        Math.round(c0[0] + t * (c1[0] - c0[0])),
        Math.round(c0[1] + t * (c1[1] - c0[1])),
        Math.round(c0[2] + t * (c1[2] - c0[2])),
        210,
      ];
    }
  }
  return [...colors[colors.length - 1], 220];
}

function interpolateSpecial(value, palette) {
  if (value <= palette[0].val) return [...palette[0].rgb, 200];
  if (value >= palette[palette.length - 1].val) return [...palette[palette.length - 1].rgb, 200];

  for (let i = 0; i < palette.length - 1; i++) {
    if (value <= palette[i + 1].val) {
      const t = (value - palette[i].val) / (palette[i + 1].val - palette[i].val);
      return [
        Math.round(palette[i].rgb[0] + t * (palette[i + 1].rgb[0] - palette[i].rgb[0])),
        Math.round(palette[i].rgb[1] + t * (palette[i + 1].rgb[1] - palette[i].rgb[1])),
        Math.round(palette[i].rgb[2] + t * (palette[i + 1].rgb[2] - palette[i].rgb[2])),
        200,
      ];
    }
  }
  return [...palette[palette.length - 1].rgb, 200];
}

/**
 * Obține categoria AQI/meteo pentru o valoare.
 */
export function getAqiCategory(value, variable) {
  const thresholds = THRESHOLDS[variable];
  const categories = getCategoriesForVariable(variable);
  if (thresholds == null || value == null || isNaN(value)) return null;

  for (let i = thresholds.length - 2; i >= 0; i--) {
    if (value >= thresholds[i]) {
      return {
        index: i,
        label: categories[Math.min(i, categories.length - 1)].label,
        color: categories[Math.min(i, categories.length - 1)].color,
      };
    }
  }
  return { index: 0, label: categories[0].label, color: categories[0].color };
}

/**
 * Generează gradientul de legendă pe un canvas.
 */
export function drawLegendGradient(canvas, variable) {
  const ctx = canvas.getContext('2d');
  const h = canvas.height;
  const w = canvas.width;
  ctx.clearRect(0, 0, w, h);

  const thresholds = THRESHOLDS[variable];
  if (!thresholds) return;

  const minVal = thresholds[0];
  const maxVal = thresholds[thresholds.length - 1];

  for (let y = 0; y < h; y++) {
    const value = maxVal - (maxVal - minVal) * (y / h);
    const [r, g, b, a] = valueToColor(value, variable);
    ctx.fillStyle = `rgba(${r},${g},${b},${a / 255})`;
    ctx.fillRect(0, y, w, 1);
  }
}

// ── Helpers ──────────────────────────────────────────────────────
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}
